import { DistributePaymentForTreaty, DistributePaymentForTreatyVariables } from "_graphql/queries/__generated__/DistributePaymentForTreaty";
import { useMatch, useNavigate, useSearch } from "react-location";
import { useProportionalPayment, useTreaty } from "hooks/data/use-treaties";

import { DistributeTreatyPaymentForm } from "components/forms";
import { LocationGenerics } from "router/location";
import { Modal } from "components";
import { TreatyType } from "_graphql/__generated__/globalTypes";
import { distributeTreatyPayment } from "_graphql/visal/queries/treaties";
import toast from "react-hot-toast";
import { useEffect } from "react";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { wrapClick } from "utils";

export default function DistributePaymentContainer({
    open,
    setOpen,
}: {
    open: boolean;
    setOpen: (val: boolean) => void;
    refetch?: () => void;
}) {
    const { params } = useMatch<LocationGenerics>();
    const searchParams = useSearch<LocationGenerics>();
    const navigate = useNavigate<LocationGenerics>();
    const { treaty, loading: fetchingTreaty } = useTreaty({
        treatyId: params.treaty || "",
    });

    const { payment, loading: fetching } = useProportionalPayment({
        treatyPaymentId: searchParams.id || "",
    });
    const [distribute, { loading }] = useMutation<
        DistributePaymentForTreaty,
        DistributePaymentForTreatyVariables
    >(distributeTreatyPayment, {
        refetchQueries: ["GetProportionalTreatyPaymentsVariables"],
    });
    const form = useFormik<any>({
        initialValues: {
            deductions: [],
        },
        onSubmit: async (variables) => {
            await distribute({
                variables: {
                    type: TreatyType.PROPORTIONAL,
                    treatyPaymentId: searchParams.id || "",
                    paymentInformation: variables.deductions.map((d: any) => ({
                        participant_payment_id: d.participant_payment_id,
                        payment_details: JSON.stringify({
                            payment_type: d.payment_type,
                            payment_from: {
                                cheque_number: d.cheque_number ? d.cheque_number : "N/A",
                                bank_name: d.bank_name,
                            },
                            payment_to: d.b_bank_name ? d.b_bank_name : "N/A",
                        }),
                        payment_comment: d.comment,
                    })),
                    treatyId: params.treaty || "",
                }
            }).then((data) => {
                if (data.data?.distributePaymentForTreaty) {
                    toast.success("Payment distributed successfully");
                    setOpen(false);
                    navigate({
                        search(prev) {
                            return {
                                ...prev,
                            };
                        },
                    });
                } else {
                    toast.error("Failed to distribute payment");
                }
            }).catch((e) => {
            });
        },
        onReset: () => {
            setOpen(false);
        },
    });


    useEffect(() => {
        if (treaty && treaty?.treaty_participants && payment && payment.treaty_participant_payment) {
            const deductions = treaty?.treaty_participants.map((participant) => {
                const share = payment?.treaty_participant_payment?.find(
                    (share) => share?.treaty_participationstreaty_participation_id === participant?.treaty_participation_id
                );
                return {
                    participant_payment_id: participant?.treaty_participation_id,
                    payment_type: "",
                    bank_name: "",
                    b_bank_name: "",
                    cheque_number: "",
                    comment: "",
                    share,
                    re_company_name: participant?.reinsurer?.re_company_name || "",
                    treaty_participation_percentage: participant?.treaty_participation_percentage || 0,
                };
            });
            form.setFieldValue("deductions", deductions);
        }
    }, [treaty, payment]);


    return (
        <Modal
            open={open}
            setOpen={() => {
                setOpen(false);
                navigate({
                    search(prev) {
                        return {
                            ...prev,
                        };
                    },
                });
            }}
            size="5xl"
            title="Dsitribute Payment"
            loading={fetching || fetchingTreaty}
            description="Distribute payment to the offer participants."
            renderActions={() => (
                <>
                    <button
                        type="button"
                        disabled={loading}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                        onClick={wrapClick(form.handleSubmit)}
                    >
                        {loading ? "Distributing payment..." : "Distribute Payment"}
                    </button>
                </>
            )}
        >
            <DistributeTreatyPaymentForm form={form} />
        </Modal>
    );
}
